import imageUrlBuilder from "@sanity/image-url";
import { ImageUrlBuilder } from "@sanity/image-url/lib/types/builder";
import {
  AutoMode,
  FitMode,
  ImageFormat,
  SanityClientLike,
  SanityImageSource,
  SanityModernClientLike,
  SanityProjectDetails
} from "@sanity/image-url/lib/types/types";
import siteConfig from "../../config/siteConfig";
import {
  NullCheckedSanityImage,
  SanityImageSourceExt
} from "../../models/shared/SanityImageSourceExt";
import { validImage } from "../../utilities/image";

const sanityConfig = {
  projectId: siteConfig.sanityProjectId ?? "",
  dataset: siteConfig.sanityDataset ?? ""
};

export type UrlOptions = {
  friendlyFilename?: boolean;
};

// Custom Sanity image URL builder class to handle Netlify image proxy URLs.
export class CustomImageUrlBuilder {
  builder: ImageUrlBuilder;

  constructor(
    options?: SanityClientLike | SanityProjectDetails | SanityModernClientLike
  ) {
    this.builder = imageUrlBuilder(options);
  }

  image(source: SanityImageSource): CustomImageUrlBuilder {
    this.builder = this.builder.image(source);
    return this;
  }
  width(width: number): CustomImageUrlBuilder {
    this.builder = this.builder.width(width);
    return this;
  }
  height(height: number): CustomImageUrlBuilder {
    this.builder = this.builder.height(height);
    return this;
  }
  size(width: number, height: number): CustomImageUrlBuilder {
    this.builder = this.builder.size(width, height);
    return this;
  }
  format(format: ImageFormat): CustomImageUrlBuilder {
    this.builder = this.builder.format(format);
    return this;
  }
  forceDownload(download: boolean | string): CustomImageUrlBuilder {
    this.builder = this.builder.forceDownload(download);
    return this;
  }
  fit(value: FitMode): CustomImageUrlBuilder {
    this.builder = this.builder.fit(value);
    return this;
  }
  auto(value: AutoMode): CustomImageUrlBuilder {
    this.builder = this.builder.auto(value);
    return this;
  }
  url(options?: UrlOptions): string {
    const { friendlyFilename } = options ?? {};

    let url = this.builder.url();

    // Rewrite image URLs to a relative path. Netlify will proxy the request back to the Sanity CDN (see createRedirects.ts::createImageRewrite).
    // Note that the proxy _only_ works on Netlify, ie not locally.
    if (process.env.GATSBY_USE_IMAGES_REVERSE_PROXY === "true") {
      url = url.replace(
        `https://cdn.sanity.io/images/${process.env.GATSBY_SANITY_PROJECT_ID}/${process.env.GATSBY_SANITY_DATASET}/`,
        "/images/"
      );
    }

    if (friendlyFilename) {
      const image = this.builder.options.source as SanityImageSourceExt;

      if (validImage(image) && image.asset.url) {
        const { url: assetUrl, originalFilename } = image.asset;

        // Note: Returns URL in the following format: "https://CDN-URL.com/IMAGE-ID.jpg/ORIGINAL-FILENAME.jpg?PARAMS"
        url = encodeURI(url.replace(assetUrl, `${assetUrl}/${originalFilename}`));
      }
    }

    return url;
  }
}

/**
 * Gets a Sanity image URL builder, but only accept images that are checked with
 * validImage() in the image utility to avoid the builder hanging when fed
 * empty assets or file assets instead of image assets.
 */
export default function imageBuilder(source: NullCheckedSanityImage) {
  const customBuilder = new CustomImageUrlBuilder(sanityConfig);
  return customBuilder.image(source);
}
